import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Colors.css";

interface ColorItem {
  id: number;
  color: string;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

// Mapping German color names to real CSS colors
const germanColorToCSSColor: Record<string, string> = {
  Rot: "red",
  Blau: "blue",
  Grün: "green",
  Gelb: "yellow",
  Orange: "orange",
  Lila: "purple",
  Rosa: "pink",
  Braun: "brown",
  Schwarz: "black",
  Weiß: "white",
  Grau: "gray",
  Gold: "gold",
  Silber: "silver",
  Türkis: "turquoise",
  Beige: "beige",
};

const Colors: React.FC = () => {
  const [data, setData] = useState<ColorItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/colors`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching colors:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: ColorItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: ColorItem) => {
    if (currentlyPlaying === item.color) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.color);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.color);
      };
    } else {
      speak(item.color);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getColorStyle = (germanColor: string) => ({
    backgroundColor: germanColorToCSSColor[germanColor] || "white",
    width: "100%",
    height: "100%",
    borderRadius: "10%",
  });

  const titleMap = {
    en: "Colors",
    de: "Farben",
    ta: "வண்ணங்கள்",
    si: "වර්ණ",
    hi: "रंग",
    ar: "ألوان",
    tr: "Renkler",
    ru: "Цвета",
    zh: "颜色",
    fr: "Couleurs",
    es: "Colores",
  };

  const subtitleMap = {
    en: "German Colors",
    de: "Deutsche Farben",
    ta: "ஜெர்மன் வண்ணங்கள்",
    si: "ජර්මන් වර්ණ",
    hi: "जर्मन रंग",
    ar: "ألوان ألمانية",
    tr: "Almanca Renkler",
    ru: "Немецкие цвета",
    zh: "德语颜色",
    fr: "Couleurs allemandes",
    es: "Colores alemanes",
  };

  return (
    <div className="colors-page-container">
      <div className="colors-page-header">
        <h1 className="colors-page-title">{titleMap["de"]}</h1>
        <p className="colors-page-subtitle">{subtitleMap[language]}</p>
      </div>

      {loading ? (
        <div className="colors-page-loading">Loading...</div>
      ) : (
        <div className="colors-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.color;
            return (
              <div
                key={item.id}
                className={`colors-page-card ${
                  isPlaying ? "colors-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="colors-page-image-wrapper">
                  <div style={getColorStyle(item.color)}></div>
                </div>
                <div className="colors-page-color-name">
                  {capitalize(item.color)}
                </div>
                <div className="colors-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="colors-page-sentence-text">
                  {item.sentence}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Colors;
